import { classes } from "utils"
import { Input } from "./input.js"
import { Dropdown } from "./dropdown.js"
import { Layout } from "./layout.js"
import { useId, useMemo } from "react"
import { typography } from "design/typography"
import { useFormContext } from "react-hook-form"
import { colors } from "design/colors"

export const dropdownInputVariablesLight = {
  "--resbird-dropdown-input-focus-border-color": colors.primaryBlueGrey,
  "--resbird-dropdown-input-error-border-color": colors.red,
  "--resbird-dropdown-input-border-color": colors.grey400,
  "--resbird-dropdown-input-label-color": colors.lightGrey100,
  "--resbird-dropdown-input-required-color": colors.red,
  "--resbird-dropdown-input-label-error": colors.red,
  "--resbird-dropdown-input-disabled-background": colors.grey200,
}

const computeStyle = ({ error, disabled }) => ({
  height: 45,
  width: "100%",
  borderRadius: 4,
  border: "1px solid",
  cursor: "pointer",
  boxSizing: "border-box",
  borderColor: error
    ? "var(--resbird-dropdown-input-error-border-color)"
    : "var(--resbird-dropdown-input-border-color)",
  ":focus-within": {
    border: "2px solid",
    borderColor: error
      ? "var(--resbird-input-error-border-color)"
      : "var(--resbird-input-focus-border-color)",
  },
  ...(disabled
    ? {
        backgroundColor: "var(--resbird-dropdown-input-disabled-background)",
        cursor: "not-allowed",
      }
    : {}),
})

export const DropdownInput = ({
  dropdownFieldName,
  dropdownRules,
  inputFieldName,
  inputRules,
  label,
  error: propsError,
  errorStyle,
  labelStyle,
  dropdownOptions = [],
  dropdownProps,
  inputProps,
  dropdownPlaceHolder,
  inputPlaceHolder,
  className,
  required,
  disabled,
  isAmount,
  disableDropdown,
  inputOnChange,
}) => {
  const id = useId()
  const errorId = useId()
  const { formState } = useFormContext()

  const error = useMemo(
    () =>
      (!_.isEmpty(inputRules) || !_.isEmpty(dropdownRules)) &&
      (propsError ||
        _.get(formState.errors, dropdownFieldName)?.message ||
        _.get(formState.errors, inputFieldName)?.message),
    [
      dropdownFieldName,
      dropdownRules,
      formState,
      inputFieldName,
      inputRules,
      propsError,
    ],
  )

  const style = useMemo(
    () => computeStyle({ error, disabled }),
    [disabled, error],
  )

  return (
    <Layout
      className="dropdown-input-layout"
      css={{
        width: "100%",
        flexWrap: "nowrap",
        alignItems: "flex-start",
      }}
      vertical
    >
      {label && (
        <label
          className="input-label"
          css={[
            {
              marginBottom: 11,
              color: "var(--resbird-input-label-color)",
            },
            typography.widgetLabel,
            labelStyle,
          ]}
          htmlFor={id}
        >
          <span>{`${label}:`}</span>
          {(dropdownRules?.required || inputRules?.required || required) && (
            <span
              className="required-indicator"
              css={{ color: "var(--resbird-dropdown-input-required-color)" }}
            >
              *
            </span>
          )}
        </label>
      )}
      <Layout
        css={[
          { background: colors.white },
          style,
          isAmount && { flexDirection: "row-reverse" },
        ]}
        disabled={disabled}
      >
        <Dropdown
          className={classes(className, "dropdown-input-select")}
          css={{ width: "max-content" }}
          fieldName={dropdownFieldName}
          options={dropdownOptions}
          placeHolder={dropdownPlaceHolder}
          dropdownStyle={{
            border: "none",
            height: 40,
            borderRadius: 0,
          }}
          rules={dropdownRules}
          errorStyle={{ display: "none" }}
          disabled={disabled || disableDropdown}
          {...dropdownProps}
        />
        <Input
          className={classes(className, "dropdown-input-value")}
          id={id}
          inputStyle={[
            {
              border: "none",
              height: 40,
              borderRadius: 0,
              ":focus-within": {
                border: "none",
              },
              clipPath:
                !disabled &&
                `polygon(
              10% 0%,    /* Top left - move inward 10% from the top of the left border */
              100% 0%,   /* Top right */
              100% 100%, /* Bottom right */
              10% 100%,  /* Move inward 10% from the bottom of the left border */
              0% 90%,    /* Clip the left border until 90% from the bottom */
              0% 10%     /* Clip the left border from the top */
            )`,
            },
            isAmount
              ? {
                  borderRight:
                    !disabled &&
                    `0.8px solid var(--resbird-dropdown-input-border-color)`,
                }
              : {
                  borderLeft:
                    !disabled &&
                    `0.8px solid var(--resbird-dropdown-input-border-color)`,
                },
          ]}
          css={{ flexGrow: 2 }}
          rules={inputRules}
          fieldName={inputFieldName}
          placeHolder={inputPlaceHolder}
          errorStyle={{ display: "none" }}
          disabled={disabled}
          isAmount={isAmount}
          onChange={inputOnChange}
          {...inputProps}
        />
      </Layout>
      {error && (
        <label
          className="error-description"
          css={[
            errorStyle,
            {
              marginTop: 5,
              fontSize: 14,
              color: "var(--resbird-dropdown-input-label-error)",
            },
          ]}
          htmlFor={id}
          id={errorId}
          role="aria-errormessage"
        >
          {error}
        </label>
      )}
    </Layout>
  )
}
